import type { Container, FederatedPointerEvent, Rectangle } from 'pixi.js';

interface PointerHandlers {
  onPointerMove: (event: FederatedPointerEvent) => void;
  onPointerDown: (event: FederatedPointerEvent) => void;
  onPointerUp: (event: FederatedPointerEvent) => void;
  onPointerUpOutside: (event: FederatedPointerEvent) => void;
  onPointerCancel: () => void;
}

interface PointerHandlerState {
  isGameEnded: () => boolean;
  isPointerDown: () => boolean;
  setPointerDown: (value: boolean) => void;
  updateBlockPosition: (x: number) => void;
  handleClick: () => void;
}

interface GlobalPointerState {
  isGameEnded: () => boolean;
  isPointerDown: () => boolean;
  updateBlockPosition: (x: number) => void;
}

export const createPointerEventHandlers = (
  state: PointerHandlerState,
): PointerHandlers => ({
  onPointerMove: (event: FederatedPointerEvent) => {
    if (state.isGameEnded() || !state.isPointerDown()) return;
    state.updateBlockPosition(event.globalX);
  },
  onPointerDown: (event: FederatedPointerEvent) => {
    if (state.isGameEnded()) return;
    if (event.button !== 0) return;
    state.updateBlockPosition(event.globalX);
    state.setPointerDown(true);
  },
  onPointerUp: (event: FederatedPointerEvent) => {
    if (event.button !== 0) return;
    state.setPointerDown(false);
    if (!state.isGameEnded()) {
      state.handleClick();
    }
  },
  onPointerUpOutside: (event: FederatedPointerEvent) => {
    if (event.button !== 0) return;
    state.setPointerDown(false);
  },
  onPointerCancel: () => {
    state.setPointerDown(false);
  },
});

export const registerContainerPointerEvents = (
  container: Container,
  handlers: PointerHandlers,
): void => {
  container.eventMode = 'static';
  container.on('pointermove', handlers.onPointerMove);
  container.on('pointerdown', handlers.onPointerDown);
  container.on('pointerup', handlers.onPointerUp);
  container.on('pointerupoutside', handlers.onPointerUpOutside);
  container.on('pointercancel', handlers.onPointerCancel);
};

export const createGlobalPointerMoveHandler = (state: GlobalPointerState) => {
  return (event: FederatedPointerEvent) => {
    if (state.isGameEnded() || !state.isPointerDown()) return;
    state.updateBlockPosition(event.globalX);
  };
};

export const registerGlobalPointerMove = (
  stage: Container,
  screen: Rectangle,
  handler: (event: FederatedPointerEvent) => void,
): void => {
  stage.eventMode = 'static';
  stage.hitArea = screen;
  stage.on('globalpointermove', handler);
};
